import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import 'react-dom';
import { _ as _slicedToArray, a as _objectSpread2, c as _extends } from './_rollupPluginBabelHelpers-b9ad31fa-7ae21c08.js';
import styled from 'styled-components';
import { s as styleInject, T as Tooltip, W as WithTooltip } from './withTooltip-92e0dfed.js';
import space from '@styled-system/space';
import color from '@styled-system/color';
import border from '@styled-system/border';
import variant from '@styled-system/variant';
import typography from '@styled-system/typography';
import { I as Icon, a as InfoIcon } from './index-28a062d6-3659b895.js';
import { C as Context } from './index-155df85b-a520c7f0.js';
import { T as Text$1 } from './text-5e5c8ea1.js';
import '@styled-system/layout';
import '@styled-system/flexbox';
import '@styled-system/position';
import '@styled-system/shadow';
import { B as Box } from './box-e581bc98.js';

var isArray = Array.isArray;

function cc(obj) {
  var out = "";

  if (typeof obj === "string" || typeof obj === "number") return obj || ""

  if (isArray(obj))
    for (var k = 0, tmp; k < obj.length; k++) {
      if ((tmp = cc(obj[k])) !== "") {
        out += (out && " ") + tmp;
      }
    }
  else
    for (var k in obj) {
      if (obj.hasOwnProperty(k) && obj[k]) out += (out && " ") + k;
    }

  return out
}

function useInput(inputDOM, onFocus, onBlur) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var handleFocus = function handleFocus(e) {
    e.preventDefault();
    setFocused(true);
    onFocus(e);
    inputDOM.current && inputDOM.current.focus();
  };
  var handleBlur = function handleBlur(e) {
    setFocused(false);
    onBlur(e);
    inputDOM.current && inputDOM.current.blur();
  };
  return {
    focused: focused,
    setFocused: setFocused,
    handleFocus: handleFocus,
    handleBlur: handleBlur
  };
}

var css_248z = ".Label_label__lzWKR {\n  display: block;\n  color: #666;\n  font-size: 12px;\n  font-weight: 600;\n  margin-bottom: 6px;\n  margin-left: 2px;\n  transition: 0.2s ease-out; }\n  .Label_label__lzWKR.Label_error__2T6RD {\n    opacity: 0.9;\n    color: #f44336; }\n\n.Label_container__3eUn3 {\n  display: flex; }\n\n.Label_tooltip__10Gf8 {\n  position: relative;\n  display: inline-block;\n  width: 16px;\n  height: 16px;\n  vertical-align: middle;\n  top: -1px;\n  margin-left: 4px; }\n\n.Label_tooltipIcon__3Sjdx {\n  width: 16px;\n  height: 16px; }\n";
var styles = {"label":"Label_label__lzWKR","error":"Label_error__2T6RD","container":"Label_container__3eUn3","tooltip":"Label_tooltip__10Gf8","tooltipIcon":"Label_tooltipIcon__3Sjdx"};
styleInject(css_248z);

var labelMode = variant({
  prop: "mode",
  scale: "labelModes",
  variants: {
    focussed: {}
  }
});

// Non customizable CSS props
var labelWrapperStyle = {
  position: "relative",
  display: "flex",
  alignItems: "center"
};
var labelStyle = {
  position: "relative"
};
var ThemedLabelWrapper = styled(Box)(labelWrapperStyle, space, color, border, typography);
var ThemedLabel = styled(Text$1)(labelStyle, space, color, border, typography, labelMode);

// Customizable CSS props
ThemedLabelWrapper.defaultProps = {
  fontFamily: "\"Proxima Nova\", sans-serif",
  mode: "default",
  variant: "default",
  padding: "zero"
};
ThemedLabel.defaultProps = {
  fontFamily: "\"Proxima Nova\", sans-serif",
  variant: "h4.semi-bold",
  marginRight: "xs"
};

var Label = function Label(_ref) {
  var children = _ref.children,
    className = _ref.className,
    focused = _ref.focused,
    error = _ref.error,
    disabled = _ref.disabled,
    tooltip = _ref.tooltip,
    themed = _ref.themed;
  if (!children) return null;
  var _React$useContext = React.useContext(Context),
    primaryColor = _React$useContext.primaryColor;
  var focusedStyle = !disabled && focused ? {
    color: primaryColor
  } : {};
  var errorClassName = error ? styles.error : "";
  var renderLabel = function renderLabel() {
    if (themed && (children || error)) {
      var mode = "default";
      if (!disabled && focused) {
        mode = "focussed";
      }
      if (error) {
        mode = "error";
      }
      return /*#__PURE__*/React.createElement(ThemedLabelWrapper, null, /*#__PURE__*/React.createElement(ThemedLabel, {
        mode: mode
      }, children), tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
        tooltip: tooltip,
        position: "TOP",
        themed: true
      }, /*#__PURE__*/React.createElement(Icon, {
        src: InfoIcon,
        size: Icon.SIZES.SMALL,
        style: {
          marginBottom: 8
        }
      })) : null);
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "".concat(styles.container)
    }, /*#__PURE__*/React.createElement("span", {
      style: focusedStyle,
      className: "".concat(styles.label, " ").concat(className, " ").concat(errorClassName)
    }, children, tooltip ? /*#__PURE__*/React.createElement(WithTooltip, {
      className: "".concat(styles.tooltip),
      tooltip: tooltip,
      position: "TOP"
    }, /*#__PURE__*/React.createElement(Icon, {
      className: "".concat(styles.tooltipIcon),
      src: InfoIcon
    })) : null), error && typeof error === "string" ? /*#__PURE__*/React.createElement("span", {
      className: "".concat(styles.label, " ").concat(errorClassName)
    }, "(".concat(error, ")")) : null);
  };
  return renderLabel();
};
Label.propTypes = {
  children: PropTypes.node || PropTypes.string,
  tooltip: PropTypes.string,
  className: PropTypes.string,
  focused: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  themed: PropTypes.bool
};
Label.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Label",
  "props": {
    "children": {
      "type": {
        "name": "custom",
        "raw": "PropTypes.node || PropTypes.string"
      },
      "required": false,
      "description": ""
    },
    "tooltip": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "className": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "focused": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "error": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "themed": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    }
  }
};

var InputMode = variant({
  prop: "mode",
  scale: "textInputModes",
  variants: {
    disabled: {}
  }
});
var WrapperMode = variant({
  prop: "mode",
  scale: "textWrapperModes",
  variants: {
    label: {}
  }
});
var InputVariant = variant({
  prop: "variant",
  scale: "textInputVariants",
  variants: {
    dropdown: {}
  }
});
var InputSize = variant({
  prop: "size",
  scale: "textInputSize",
  variants: {
    small: {}
  }
});

// Non customizable CSS props
var inputStyle = function inputStyle(props) {
  var theme = props.theme,
    multiline = props.multiline,
    hasPostfixComponent = props.hasPostfixComponent,
    focused = props.focused;
  var radii = theme.radii,
    borderWidths = theme.borderWidths,
    colors = theme.colors,
    space = theme.space;
  var paddingTopBottom = "".concat(space.lg - space.xs, "px"); // 10px
  var paddingRight = hasPostfixComponent ? "".concat(3 * space.lg - space.xs, "px") : "".concat(space.sm, "px"); // 8px
  var paddingLeft = "".concat(space.sm, "px");

  // adds left padding when there's an icon, applies only if it's an input element
  var textInputStyle = !multiline ? {
    "&:not(:first-child)": {
      paddingLeft: 3 * space.lg - space.xs // 38px
    }
  } : {};
  return _objectSpread2({
    borderRadius: radii.sm,
    border: "".concat(borderWidths.xs, "px solid ").concat(focused ? colors.blue.haptik : colors.grey.haptik),
    backgroundColor: colors.grey.pastel,
    boxSizing: "border-box",
    width: "100%",
    padding: "".concat(paddingTopBottom, " ").concat(paddingRight, " ").concat(paddingTopBottom, " ").concat(paddingLeft),
    transition: "border 0.3s ease, color 0.3s ease",
    outline: "none",
    "&:focus": {
      border: "".concat(borderWidths.xs, "px solid ").concat(colors.blue.haptik)
    },
    "&:hover": {
      border: "".concat(borderWidths.xs, "px solid ").concat(colors.blue.haptik, " !important") // This is needed, for now.
    },

    resize: "none",
    overflowY: "hidden",
    color: colors.black.haptik,
    "&::-webkit-input-placeholder": {
      color: colors.grey.dark
    }
  }, textInputStyle);
};
var inputWrapperStyle = {
  position: "relative",
  outline: "none"
};
var ThemedTextInputWrapper = styled(Box)(inputWrapperStyle, space, color, border, typography, WrapperMode);
var ThemedTextInput = styled("input")(inputStyle, space, color, border, typography, InputMode, InputVariant, InputSize);
var ThemedTextarea = styled("textarea")(inputStyle, space, color, border, typography, InputMode, WrapperMode);

// Customizable CSS props
ThemedTextInputWrapper.defaultProps = {
  fontFamily: "\"Proxima Nova\", sans-serif",
  mode: "default",
  variant: "default",
  padding: "zero"
};
ThemedTextInput.defaultProps = {
  fontFamily: "\"Proxima Nova\", sans-serif",
  mode: "default",
  variant: "default",
  fontSize: "2"
};
ThemedTextarea.defaultProps = {
  fontFamily: "\"Proxima Nova\", sans-serif",
  mode: "default",
  fontSize: "2"
};

var css_248z$1 = ".TextInput_inputWrapper__3azwH {\n  border: 1px solid #ddd;\n  border-radius: 8px;\n  overflow: hidden;\n  position: relative;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  background-color: #fff;\n  padding: 0 8px;\n  box-sizing: border-box;\n  outline: none;\n  transition: 0.2s ease-out;\n  background: #fff; }\n  .TextInput_inputWrapper__3azwH.TextInput_error__mZn5f {\n    border-color: #f44336;\n    background-color: rgba(244, 67, 54, 0.05); }\n  .TextInput_inputWrapper__3azwH.TextInput_disabled__1n0Y3 {\n    background-color: #eee; }\n  .TextInput_inputWrapper__3azwH .TextInput_input__KxzGk {\n    width: 100%; }\n  .TextInput_inputWrapper__3azwH input,\n  .TextInput_inputWrapper__3azwH textarea {\n    display: inline-block;\n    vertical-align: middle;\n    font-size: 12px;\n    border-radius: 3px;\n    outline: none;\n    border: none;\n    padding: 4px 0;\n    width: 100%;\n    line-height: 1.9;\n    background-color: transparent;\n    resize: none; }\n    .TextInput_inputWrapper__3azwH input::placeholder,\n    .TextInput_inputWrapper__3azwH textarea::placeholder {\n      color: #000;\n      opacity: 0.3; }\n    .TextInput_inputWrapper__3azwH input.TextInput_error__mZn5f,\n    .TextInput_inputWrapper__3azwH textarea.TextInput_error__mZn5f {\n      border-color: #f44336;\n      background-color: rgba(244, 67, 54, 0); }\n";
var styles$1 = {"inputWrapper":"TextInput_inputWrapper__3azwH","error":"TextInput_error__mZn5f","disabled":"TextInput_disabled__1n0Y3","input":"TextInput_input__KxzGk"};
styleInject(css_248z$1);

var TextInput = function TextInput(_ref) {
  var className = _ref.className,
    onKeyDown = _ref.onKeyDown,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    onMouseDown = _ref.onMouseDown,
    innerRef = _ref.innerRef,
    value = _ref.value,
    onChange = _ref.onChange,
    focused = _ref.focused,
    error = _ref.error,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? "Type here..." : _ref$placeholder,
    inputRef = _ref.inputRef,
    disabled = _ref.disabled,
    editable = _ref.editable,
    multiline = _ref.multiline,
    prefixClassName = _ref.prefixClassName,
    prefixComponent = _ref.prefixComponent,
    postfixComponent = _ref.postfixComponent,
    themed = _ref.themed,
    variant = _ref.variant,
    label = _ref.label,
    style = _ref.style,
    size = _ref.size,
    controlledInput = _ref.controlledInput;
  var input = React.useRef(null);
  var _React$useContext = React.useContext(Context),
    primaryColor = _React$useContext.primaryColor;
  var focusedStyle = !disabled && focused ? {
    borderColor: primaryColor
  } : {};
  var focusedClassNameString = focused ? "focused" : "";
  var errorClassName = error ? styles$1.error : "";
  var disabledClassName = disabled ? styles$1.disabled : "";
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    textValue = _useState2[0],
    setTextValue = _useState2[1];
  var handleRef = React.useCallback(function (ref) {
    input.current = ref;
    if (typeof inputRef === "function") inputRef(ref);else inputRef.current = ref;
  });
  var handleChange = function handleChange(e) {
    if (disabled) return;
    onChange(e.target.value);
    if (!controlledInput) {
      setTextValue(e.target.value);
    }
    if (multiline && input.current) {
      input.current.style.height = "auto";
      input.current.style.height = "".concat(input.current.scrollHeight, "px");
    }
  };
  var renderInput = function renderInput() {
    return multiline ? /*#__PURE__*/React.createElement("textarea", {
      disabled: disabled || !editable,
      value: textValue,
      onChange: handleChange,
      placeholder: placeholder,
      type: "text",
      ref: handleRef,
      className: "".concat(prefixClassName, "-input"),
      "data-testid": "input-textarea"
    }) : /*#__PURE__*/React.createElement("input", {
      disabled: disabled || !editable,
      value: textValue,
      onChange: handleChange,
      placeholder: placeholder,
      "data-testid": "inputText",
      type: "text",
      ref: handleRef,
      className: "".concat(prefixClassName, "-input")
    });
  };
  useEffect(function () {
    setTextValue(value);
  }, [value]);
  var classNames = {
    main: cc([styles$1.inputWrapper, className, prefixClassName, focusedClassNameString, errorClassName, disabledClassName])
  };
  var renderInputContainer = function renderInputContainer() {
    if (themed) {
      var renderElement = null;
      var mode = null;
      var inputWrapperMode = "default";
      var isDropdown = variant === "dropdown" || variant === "dropdownActive" || variant === "dropdownActiveTop";
      if (disabled) {
        mode = "disabled";
      }
      if (error) {
        mode = "error";
      }
      if (isDropdown) {
        inputWrapperMode = "dropdown";
      }
      if (label || error) {
        inputWrapperMode = "label";
      }
      var defaultInputProps = {
        disabled: disabled || !editable,
        value: textValue,
        onChange: handleChange,
        placeholder: placeholder || "type here...",
        type: "text",
        ref: isDropdown ? innerRef : handleRef,
        variant: variant,
        mode: mode,
        style: style,
        size: size,
        focused: focused
      };
      var additionalInputProps = {
        onFocus: onFocus,
        onBlur: onBlur,
        onMouseDown: onMouseDown,
        onKeyDown: onKeyDown
      };
      var allInputProps = _objectSpread2(_objectSpread2({}, defaultInputProps), additionalInputProps);
      if (!multiline) {
        if (isDropdown) {
          renderElement = /*#__PURE__*/React.createElement(ThemedTextInputWrapper, _extends({
            mode: inputWrapperMode
          }, additionalInputProps, {
            ref: innerRef,
            tabIndex: "0"
          }), prefixComponent, /*#__PURE__*/React.createElement(ThemedTextInput, _extends({}, defaultInputProps, {
            "data-testid": "inputText",
            ref: handleRef
          })), postfixComponent);
        } else {
          renderElement = /*#__PURE__*/React.createElement(ThemedTextInputWrapper, {
            mode: inputWrapperMode
          }, prefixComponent, /*#__PURE__*/React.createElement(ThemedTextInput, _extends({}, allInputProps, {
            "data-testid": "inputText",
            hasPostfixComponent: !!postfixComponent
          })), postfixComponent);
        }
      } else {
        renderElement = /*#__PURE__*/React.createElement(ThemedTextarea, _extends({}, allInputProps, {
          ref: handleRef,
          multiline: true,
          mode: label || error ? "label" : "default",
          "data-testid": "input-textarea"
        }));
      }
      return renderElement;
    }
    return /*#__PURE__*/React.createElement("div", {
      ref: innerRef,
      tabIndex: "0",
      onFocus: onFocus,
      onBlur: onBlur,
      onMouseDown: onMouseDown,
      onKeyDown: onKeyDown,
      className: classNames.main,
      style: focusedStyle
    }, prefixComponent, /*#__PURE__*/React.createElement("div", {
      className: "".concat(styles$1.input, " ").concat(prefixClassName, "-inner")
    }, renderInput()), postfixComponent);
  };
  return renderInputContainer();
};
TextInput.propTypes = {
  className: PropTypes.string,
  editable: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  focused: PropTypes.bool,
  innerRef: PropTypes.func,
  inputRef: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseDown: PropTypes.func,
  placeholder: PropTypes.string,
  postfixComponent: PropTypes.node,
  prefixClassName: PropTypes.string,
  prefixComponent: PropTypes.node,
  value: PropTypes.string,
  multiline: PropTypes.bool,
  themed: PropTypes.bool,
  variant: PropTypes.string,
  label: PropTypes.string,
  style: PropTypes.object,
  controlledInput: PropTypes.bool
};
TextInput.defaultProps = {
  className: "",
  editable: true,
  disabled: false,
  error: false,
  focused: false,
  multiline: false,
  innerRef: function innerRef() {},
  inputRef: function inputRef() {},
  onBlur: function onBlur() {},
  onChange: function onChange() {},
  onFocus: function onFocus() {},
  onKeyDown: function onKeyDown() {},
  onMouseDown: function onMouseDown() {},
  placeholder: "",
  prefixClassName: "",
  value: "",
  themed: false,
  variant: "",
  label: "",
  style: {},
  controlledInput: false
};
TextInput.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "TextInput",
  "props": {
    "placeholder": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "className": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "editable": {
      "defaultValue": {
        "value": "true",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "disabled": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "error": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "focused": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "multiline": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "innerRef": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "inputRef": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onBlur": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onChange": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onFocus": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onKeyDown": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseDown": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "prefixClassName": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "value": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "themed": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "variant": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "label": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "style": {
      "defaultValue": {
        "value": "{}",
        "computed": false
      },
      "type": {
        "name": "object"
      },
      "required": false,
      "description": ""
    },
    "controlledInput": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "postfixComponent": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "prefixComponent": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    }
  }
};

var css_248z$1$1 = ".Text_text__bC4ty {\n  flex: 1;\n  position: relative; }\n\n.Text_textWithMargin__cLdsB {\n  margin-right: 16px;\n  outline: none; }\n\n.Text_textActive__2CsGe .Text_textLabel__svN-B {\n  color: #124fa0; }\n\n.Text_textActive__2CsGe .Text_textInputWrapper___tssz {\n  border-color: #124fa0;\n  background-color: rgba(18, 79, 160, 0.05); }\n\n.Text_textError__c9bS_ .Text_textLabel__svN-B {\n  color: #f44336; }\n\n.Text_textError__c9bS_ .Text_textInputWrapper___tssz {\n  border-color: #f44336;\n  background-color: rgba(244, 67, 54, 0.05); }\n\n.Text_emojiIcon__Vppvp {\n  cursor: pointer; }\n\n.Text_textLabelWrapper__1CQ2P {\n  display: flex;\n  align-items: center;\n  margin-bottom: 6px; }\n\n.Text_textLabel__svN-B {\n  display: block;\n  color: #666;\n  font-size: 12px;\n  font-weight: 600;\n  margin-left: 2px;\n  transition: 0.2s ease-out;\n  margin-right: 8px; }\n\n.Text_speakerIcon__1Vlwm {\n  cursor: pointer;\n  color: #666;\n  width: 16px;\n  height: 16px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-weight: 600;\n  transition: 0.2s ease-out;\n  opacity: 0.6; }\n  .Text_speakerIcon__1Vlwm:hover {\n    opacity: 1; }\n  .Text_speakerIcon__1Vlwm #Text_wave3__WPCeW {\n    opacity: 0; }\n  .Text_speakerIcon__1Vlwm svg path {\n    transition: 1s ease-out; }\n  .Text_speakerIcon__1Vlwm.Text_speaking__2d56A {\n    opacity: 1; }\n    .Text_speakerIcon__1Vlwm.Text_speaking__2d56A svg {\n      overflow: visible; }\n      .Text_speakerIcon__1Vlwm.Text_speaking__2d56A svg path {\n        fill: #0d3872; }\n    .Text_speakerIcon__1Vlwm.Text_speaking__2d56A svg #wave1 {\n      animation: Text_soundwave__2D42v 1s ease-out infinite;\n      animation-delay: 0.2s; }\n    .Text_speakerIcon__1Vlwm.Text_speaking__2d56A svg #wave2 {\n      animation: Text_soundwave__2D42v 1s ease-out infinite;\n      animation-delay: 0.4s; }\n    .Text_speakerIcon__1Vlwm.Text_speaking__2d56A svg #wave3 {\n      animation: Text_soundwave__2D42v 1s ease-out infinite;\n      animation-delay: 0.6s; }\n\n.Text_textHint__3G0qQ {\n  display: block;\n  color: #999;\n  font-size: 13px;\n  font-weight: 400;\n  margin-bottom: 8px;\n  margin-left: 2px;\n  margin-top: -6px;\n  transition: 0.2s ease-out; }\n\n.Text_inputWrapper__dbWLO {\n  display: flex;\n  align-items: center;\n  margin-bottom: 8px; }\n\n.Text_textInputWrapper___tssz {\n  flex: 1;\n  border: 1px solid #ddd;\n  border-radius: 8px;\n  overflow: hidden;\n  background-color: #fff;\n  transition: 0.2s ease-out; }\n  .Text_textInputWrapper___tssz.Text_disabled__2zejI {\n    pointer-events: none;\n    background-color: #eee; }\n\n.Text_textInput__3KJ_o {\n  border: none !important;\n  outline: none !important;\n  padding: 4px 8px !important;\n  width: 100%;\n  box-sizing: border-box;\n  font-size: 14px !important;\n  resize: none;\n  background-color: transparent !important;\n  box-shadow: none !important; }\n  .Text_textInput__3KJ_o.Text_multiline__A2kXG {\n    min-height: 64px; }\n  .Text_textInput__3KJ_o.Text_disabled__2zejI {\n    color: #999; }\n  .Text_textInput__3KJ_o:not(.Text_multiline__A2kXG) {\n    height: auto !important; }\n  .Text_textInput__3KJ_o::placeholder {\n    color: #000;\n    opacity: 0.3; }\n\n.Text_textInputSubtext__TMZ95 {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  margin: 0 4px;\n  font-size: 12px;\n  position: absolute;\n  bottom: 0;\n  transform: translate3d(0, 100%, 0);\n  padding-top: 4px; }\n\n.Text_textInputSubtextCount__12bUy {\n  opacity: 0.5; }\n\n.Text_textInputSubtextError__hKV5p {\n  color: #ff0c0c;\n  opacity: 0.8; }\n\n@keyframes Text_soundwave__2D42v {\n  0% {\n    opacity: 0;\n    transform: translate3d(-5px, 0, 0); }\n  50% {\n    opacity: 1;\n    transform: translate3d(5px, 0, 0); }\n  100% {\n    opacity: 0;\n    transform: translate3d(10px, 0, 0); } }\n";
var styles$1$1 = {"text":"Text_text__bC4ty","textWithMargin":"Text_textWithMargin__cLdsB","textActive":"Text_textActive__2CsGe","textLabel":"Text_textLabel__svN-B","textInputWrapper":"Text_textInputWrapper___tssz","textError":"Text_textError__c9bS_","emojiIcon":"Text_emojiIcon__Vppvp","textLabelWrapper":"Text_textLabelWrapper__1CQ2P","speakerIcon":"Text_speakerIcon__1Vlwm","wave3":"Text_wave3__WPCeW","speaking":"Text_speaking__2d56A","soundwave":"Text_soundwave__2D42v","textHint":"Text_textHint__3G0qQ","inputWrapper":"Text_inputWrapper__dbWLO","disabled":"Text_disabled__2zejI","textInput":"Text_textInput__3KJ_o","multiline":"Text_multiline__A2kXG","textInputSubtext":"Text_textInputSubtext__TMZ95","textInputSubtextCount":"Text_textInputSubtextCount__12bUy","textInputSubtextError":"Text_textInputSubtextError__hKV5p"};
styleInject(css_248z$1$1);

var PrePostFixWrapper = styled("div")({
  position: "absolute",
  top: 0,
  height: "100%",
  display: "flex",
  alignItems: "center",
  transition: "transform 0.3s ease"
});
var PrefixComponentWrapper = styled(PrePostFixWrapper)({
  left: "8px"
});
var PostfixComponentWrapper = styled(PrePostFixWrapper)({
  right: "8px"
});
var Text = function Text(_ref) {
  var value = _ref.value,
    onChange = _ref.onChange,
    label = _ref.label,
    placeholder = _ref.placeholder,
    className = _ref.className,
    prefixClassName = _ref.prefixClassName,
    error = _ref.error,
    tooltip = _ref.tooltip,
    prefixComponent = _ref.prefixIcon,
    postfixComponent = _ref.postfixIcon,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    onKeyDown = _ref.onKeyDown,
    disabled = _ref.disabled,
    multiline = _ref.multiline,
    innerRef = _ref.innerRef,
    themed = _ref.themed,
    style = _ref.style,
    size = _ref.size,
    manuallyFocused = _ref.manuallyFocused,
    controlledInput = _ref.controlledInput;
  var _TextInputDOM = useRef();
  var _useInput = useInput(_TextInputDOM, onFocus, onBlur),
    focused = _useInput.focused,
    setFocused = _useInput.setFocused,
    handleFocus = _useInput.handleFocus,
    handleBlur = _useInput.handleBlur;
  var errorClassName = error ? "error" : "";
  var disabledClassName = disabled ? "disabled" : "";
  var handleRef = React.useCallback(function (ref) {
    _TextInputDOM.current = ref;
    if (typeof innerRef === "function") innerRef(ref);else innerRef.current = ref;
  });
  var PrefixComponent = prefixComponent ? /*#__PURE__*/React.createElement(PrefixComponentWrapper, null, prefixComponent) : null;
  var PostfixComponent = postfixComponent ? /*#__PURE__*/React.createElement(PostfixComponentWrapper, null, postfixComponent) : null;
  var inputFocused = manuallyFocused || focused;
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(styles$1$1.text, " ").concat(prefixClassName, " ").concat(className, " ").concat(errorClassName, " ").concat(disabledClassName),
    "data-testid": "text"
  }, /*#__PURE__*/React.createElement(Label, {
    focused: inputFocused,
    error: error,
    className: "".concat(styles$1$1.dropdownLabel, " ").concat(prefixClassName, "-label"),
    disabled: disabled,
    tooltip: tooltip,
    themed: themed
  }, /*#__PURE__*/React.createElement("span", {
    "data-testid": "labelText"
  }, label)), /*#__PURE__*/React.createElement(TextInput, {
    inputRef: handleRef,
    prefixClassName: prefixClassName,
    className: "".concat(prefixClassName, "-textinput"),
    onFocus: handleFocus,
    onBlur: handleBlur,
    onKeyDown: onKeyDown,
    onChange: onChange,
    onClick: setFocused,
    focused: inputFocused,
    active: inputFocused,
    value: value,
    placeholder: placeholder,
    disabled: disabled,
    error: error,
    multiline: multiline,
    themed: themed,
    label: label,
    style: style,
    size: size,
    prefixComponent: themed && PrefixComponent,
    postfixComponent: themed && PostfixComponent,
    controlledInput: controlledInput
  }), themed && error && /*#__PURE__*/React.createElement(Text$1, {
    variant: "h4.semi-bold",
    color: "red.haptik"
  }, error));
};
Text.propTypes = {
  /** Place Holder Text for an Empty Text Field */
  placeholder: PropTypes.string,
  /** Classname for the Parent element */
  className: PropTypes.string,
  /** Value of the Text Field */
  value: PropTypes.string,
  /** Label for the input */
  label: PropTypes.string,
  /** Error to be displayed (if any) */
  error: PropTypes.string,
  /** onChange Callback */
  onChange: PropTypes.func,
  /** A classname prepended on parent */
  prefixClassName: PropTypes.string,
  /** onFocus Callback */
  onFocus: PropTypes.func,
  /** onBlur Callback */
  onBlur: PropTypes.func,
  /** onKeyDown Callback */
  onKeyDown: PropTypes.func,
  /** Disable Flag for input */
  disabled: PropTypes.bool,
  /** Tooltip to help user with the input fields */
  tooltip: PropTypes.string,
  /** If you want to render a textarea instead */
  multiline: PropTypes.bool,
  /** Passing Ref for input field reference */
  innerRef: PropTypes.func,
  /** Flag to determine if text input is themed */
  themed: PropTypes.bool,
  /** Overrides styles of the core input element */
  style: PropTypes.object,
  /** Size of dropdown */
  size: PropTypes.string,
  /** component to be rendered before input box */
  prefixIcon: PropTypes.node,
  /** component to be rendered after input box */
  postfixIcon: PropTypes.node,
  /** flag to control updation of input display value only via value prop */
  controlledInput: PropTypes.bool,
  /** when an input needs to be focused manually */
  manuallyFocused: PropTypes.bool
};
Text.defaultProps = {
  placeholder: "",
  className: "",
  value: "",
  label: "",
  error: "",
  tooltip: "",
  size: "",
  disabled: false,
  multiline: false,
  onChange: function onChange() {},
  onFocus: function onFocus() {},
  onBlur: function onBlur() {},
  onKeyDown: function onKeyDown() {},
  innerRef: function innerRef() {},
  themed: false,
  style: {},
  controlledInput: false,
  manuallyFocused: false
};
Text.classNames = {
  $prefix: "Prefix ClassName added to the Parent",
  "$prefix-label": "Added to the Label Component",
  "$prefix-textinput": "Added to the Text Input Component",
  "$prefix-input": "Added to the input DOM Node"
};
Text.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Text",
  "props": {
    "placeholder": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Place Holder Text for an Empty Text Field"
    },
    "className": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Classname for the Parent element"
    },
    "value": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Value of the Text Field"
    },
    "label": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Label for the input"
    },
    "error": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Error to be displayed (if any)"
    },
    "tooltip": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Tooltip to help user with the input fields"
    },
    "size": {
      "defaultValue": {
        "value": "\"\"",
        "computed": false
      },
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Size of dropdown"
    },
    "disabled": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Disable Flag for input"
    },
    "multiline": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "If you want to render a textarea instead"
    },
    "onChange": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "onChange Callback"
    },
    "onFocus": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "onFocus Callback"
    },
    "onBlur": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "onBlur Callback"
    },
    "onKeyDown": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "onKeyDown Callback"
    },
    "innerRef": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Passing Ref for input field reference"
    },
    "themed": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Flag to determine if text input is themed"
    },
    "style": {
      "defaultValue": {
        "value": "{}",
        "computed": false
      },
      "type": {
        "name": "object"
      },
      "required": false,
      "description": "Overrides styles of the core input element"
    },
    "controlledInput": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "flag to control updation of input display value only via value prop"
    },
    "manuallyFocused": {
      "defaultValue": {
        "value": "false",
        "computed": false
      },
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "when an input needs to be focused manually"
    },
    "prefixClassName": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "A classname prepended on parent"
    },
    "prefixIcon": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "component to be rendered before input box"
    },
    "postfixIcon": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "component to be rendered after input box"
    }
  }
};

export { Label as L, Text as T, Label as a, TextInput as b, cc as c, inputStyle as i, useInput as u };
